// COGS drill-down: exact purchase rows behind Cost of Goods / Paid Cash / Paid Bank / Outstanding + supplier payments (read-only).
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { slug } from '@/lib/exportCsv';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExportTrio } from '@/components/accounting/ExportTrio';

const n = (v) => Number(v || 0);
const fm = (v) => `${cur()} ${n(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money = (v) => n(v).toFixed(2);
const TABS = [
  { key: 'all', label: 'Cost of Goods', summary: (b) => b.cogs },
  { key: 'cash', label: 'Paid cash', summary: (b) => b.cogs_breakdown?.paid_cash },
  { key: 'bank', label: 'Paid bank', summary: (b) => b.cogs_breakdown?.paid_bank },
  { key: 'credit', label: 'Outstanding (credit)', summary: (b) => b.cogs_breakdown?.unpaid_credit },
  { key: 'payments', label: "Payments applied to this branch's bills", summary: null },
];
const ROW_COLS = [
  { key: 'date', label: 'Date' }, { key: 'supplier', label: 'Supplier' }, { key: 'invoice_number', label: 'Invoice' }, { key: 'branch', label: 'Branch' },
  { key: 'category', label: 'Category' }, { key: 'payment_mode', label: 'Method' }, { key: 'amount', label: 'Amount', numeric: true, format: money },
  { key: 'paid', label: 'Paid', numeric: true, format: money }, { key: 'remaining', label: 'Remaining', numeric: true, format: money },
  { key: 'paid_basis', label: 'Paid basis', format: (v) => (v === 'fifo_estimate' || v === 'fifo_cross_branch' ? 'FIFO estimate' : v) },
];
const PAY_COLS = [
  { key: 'payment_date', label: 'Payment date' }, { key: 'supplier', label: 'Supplier' }, { key: 'invoice_number', label: 'Bill no.' }, { key: 'bill_date', label: 'Bill date' },
  { key: 'period', label: 'Bill period' }, { key: 'payment_mode', label: 'Method' }, { key: 'amount', label: 'Paid', numeric: true, format: money },
  { key: 'bill_remaining', label: 'Bill remaining', numeric: true, format: money },
  { key: 'basis', label: 'Basis', format: (v) => (v === 'linked' ? 'Linked to bill' : 'FIFO estimate') },
];

export function CogsDetailDialog({ b, start, end, tab, onTab, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!tab) return;
    if (data) return;
    api.get('/accounting/branch-cogs-rows', { params: { branch_id: b.branch_id, start_date: start, end_date: end } })
      .then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!tab) return null;
  const t = TABS.find((x) => x.key === tab);
  const isPay = tab === 'payments';
  const rows = !data ? [] : isPay ? data.supplier_payments : data.rows.filter((r) => tab === 'all' || r.bucket === tab);
  const total = Math.round(rows.reduce((a, r) => a + n(r.amount), 0) * 100) / 100;
  const shown = t.summary ? n(t.summary(b)) : null;
  const ok = shown == null || Math.abs(total - shown) < 0.01;
  const cols = isPay ? PAY_COLS : ROW_COLS;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="cogs-detail-dialog">
        <DialogHeader><DialogTitle>{b.branch_name} · {start} to {end}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-1">
          {TABS.map((x) => (
            <button key={x.key} type="button" onClick={() => onTab(x.key)} data-testid={`cogs-tab-${x.key}`}
              className={`rounded-full px-2.5 py-0.5 text-[11px] border transition-colors ${tab === x.key ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-muted'}`}>{x.label}</button>
          ))}
        </div>
        {err && <div className="text-xs text-rose-700">{err}</div>}
        {!data && !err && <div className="text-xs text-muted-foreground">Loading…</div>}
        {data && (
          <>
            <div className={`rounded-md px-3 py-2 text-xs ${ok ? 'bg-emerald-50 text-emerald-900' : 'bg-rose-50 text-rose-900'}`} data-testid="cogs-check-result">
              {shown == null
                ? `${rows.length} payment lines · ${fm(total)} (current-period bills ${fm(data.supplier_payment_totals['current-period bill'])} · older bills ${fm(data.supplier_payment_totals['older bill'])}). ${data.note}`
                : ok ? `Matches: ${rows.length} transactions total ${fm(total)}, same as the summary.`
                  : `Does NOT match: summary ${fm(shown)} vs transactions ${fm(total)} (difference ${fm(total - shown)}).`}
              {tab === 'credit' && <div className="text-[10px] mt-0.5">Of these credit purchases, still unpaid: {fm(data.totals.credit_remaining)} (payments matched oldest bill first unless linked — estimate).</div>}
            </div>
            <div className="max-h-80 overflow-auto border border-border rounded-md">
              <table className="w-full text-[11px]">
                <thead className="bg-muted sticky top-0"><tr>{cols.map((c) => <th key={c.key} className={`px-2 py-1 ${c.numeric ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-border/50">{cols.map((c) => <td key={c.key} className={`px-2 py-0.5 ${c.numeric ? 'text-right font-mono' : ''}`}>{c.format ? c.format(r[c.key], r) : r[c.key]}</td>)}</tr>
                  ))}
                  {!rows.length && <tr><td colSpan={cols.length} className="px-2 py-3 text-center text-muted-foreground">No transactions</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end items-center gap-2">
              <Button variant="outline" size="sm" onClick={onClose} data-testid="cogs-detail-close">Close</Button>
              <ExportTrio testId="cogs-detail-export" disabled={!rows.length} spec={() => ({ title: `${t.label} — ${b.branch_name}`, subtitle: `${start} to ${end}`,
                filename: `${slug(t.label)}_${slug(b.branch_name)}_${start}_${end}`, cols, rows: [...rows, { [cols[0].key]: 'TOTAL', amount: total }] })} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
